// ============================================
// Shared GraphQL Fragments
// ============================================

export const IMAGE_FRAGMENT = /* GraphQL */ `
  fragment ImageFields on Image {
    url
    altText
    width
    height
  }
`;

export const MONEY_FRAGMENT = /* GraphQL */ `
  fragment MoneyFields on MoneyV2 {
    amount
    currencyCode
  }
`;

export const SEO_FRAGMENT = /* GraphQL */ `
  fragment SeoFields on SEO {
    title
    description
  }
`;

export const PRODUCT_VARIANT_FRAGMENT = /* GraphQL */ `
  fragment ProductVariantFields on ProductVariant {
    id
    title
    availableForSale
    selectedOptions {
      name
      value
    }
    price {
      ...MoneyFields
    }
    compareAtPrice {
      ...MoneyFields
    }
    image {
      ...ImageFields
    }
    sku
  }
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
`;

export const PRODUCT_FRAGMENT = /* GraphQL */ `
  fragment ProductFields on Product {
    id
    handle
    title
    description
    descriptionHtml
    vendor
    productType
    tags
    availableForSale
    options {
      id
      name
      optionValues {
        name
        swatch {
          color
          image {
            previewImage {
              url
            }
          }
        }
      }
      values
    }
    priceRange {
      minVariantPrice {
        ...MoneyFields
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        ...MoneyFields
      }
    }
    variants(first: 250) {
      edges {
        node {
          ...ProductVariantFields
        }
      }
    }
    images(first: $imageCount) {
      edges {
        node {
          ...ImageFields
        }
      }
    }
    featuredImage {
      ...ImageFields
    }
    seo {
      ...SeoFields
    }
    createdAt
    updatedAt
  }
  ${PRODUCT_VARIANT_FRAGMENT}
  ${SEO_FRAGMENT}
`;

export const PRODUCT_CARD_FRAGMENT = /* GraphQL */ `
  fragment ProductCardFields on Product {
    id
    handle
    title
    vendor
    productType
    tags
    availableForSale
    priceRange {
      minVariantPrice {
        ...MoneyFields
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        ...MoneyFields
      }
    }
    featuredImage {
      ...ImageFields
    }
    images(first: 2) {
      edges {
        node {
          ...ImageFields
        }
      }
    }
    options {
      id
      name
      optionValues {
        name
        swatch {
          color
          image {
            previewImage {
              url
            }
          }
        }
      }
      values
    }
    variants(first: 1) {
      edges {
        node {
          id
          title
          availableForSale
          selectedOptions {
            name
            value
          }
          price {
            ...MoneyFields
          }
          compareAtPrice {
            ...MoneyFields
          }
        }
      }
    }
    sellingPlanGroups(first: 5) {
      edges {
        node {
          name
          appName
          options {
            name
            values
          }
          sellingPlans(first: 10) {
            edges {
              node {
                id
                name
                recurringDeliveries
                options {
                  name
                  value
                }
                priceAdjustments {
                  orderCount
                  adjustmentValue {
                    ... on SellingPlanPercentagePriceAdjustment {
                      adjustmentPercentage
                    }
                    ... on SellingPlanFixedAmountPriceAdjustment {
                      adjustmentAmount {
                        ...MoneyFields
                      }
                    }
                    ... on SellingPlanFixedPriceAdjustment {
                      price {
                        ...MoneyFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    createdAt
  }
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
`;

export const PREDICTIVE_PRODUCT_CARD_FRAGMENT = /* GraphQL */ `
  fragment PredictiveProductCardFields on Product {
    id
    handle
    title
    tags
    availableForSale
    priceRange {
      minVariantPrice {
        ...MoneyFields
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        ...MoneyFields
      }
    }
    featuredImage {
      ...ImageFields
    }
  }
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
`;

export const COLLECTION_FRAGMENT = /* GraphQL */ `
  fragment CollectionFields on Collection {
    id
    handle
    title
    description
    descriptionHtml
    image {
      ...ImageFields
    }
    seo {
      ...SeoFields
    }
    updatedAt
  }
  ${IMAGE_FRAGMENT}
  ${SEO_FRAGMENT}
`;

export const CART_FRAGMENT = /* GraphQL */ `
  fragment CartFields on Cart {
    id
	updatedAt
    checkoutUrl
    totalQuantity
    buyerIdentity {
      email
      phone
      countryCode
    }
    attributes {
      key
      value
    }
    discountCodes {
      code
      applicable
    }
    cost {
      subtotalAmount {
        ...MoneyFields
      }
      totalAmount {
        ...MoneyFields
      }
      totalTaxAmount {
        ...MoneyFields
      }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          cost {
            totalAmount {
              ...MoneyFields
            }
            amountPerQuantity {
              ...MoneyFields
            }
            compareAtAmountPerQuantity {
              ...MoneyFields
            }
          }
          discountAllocations {
            discountedAmount {
              ...MoneyFields
            }
            ... on CartAutomaticDiscountAllocation {
              title
            }
            ... on CartCodeDiscountAllocation {
              code
            }
          }
          attributes {
            key
            value
          }
          sellingPlanAllocation {
            sellingPlan {
              id
              name
              options {
                name
                value
              }
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              title
              selectedOptions {
                name
                value
              }
			  product {
				id
				handle
				title
				vendor
				productType
				featuredImage {
				  ...ImageFields
				}
			  }
              price {
                ...MoneyFields
              }
              image {
                ...ImageFields
              }
              sellingPlanAllocations(first: 10) {
                edges {
                  node {
                    sellingPlan {
                      id
                      name
                      recurringDeliveries
                      options {
                        name
                        value
                      }
                    }
                    priceAdjustments {
                      price {
                        ...MoneyFields
                      }
                      compareAtPrice {
                        ...MoneyFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${MONEY_FRAGMENT}
  ${IMAGE_FRAGMENT}
`;

export const PAGE_FRAGMENT = /* GraphQL */ `
  fragment PageFields on Page {
    id
    handle
    title
    body
    bodySummary
    seo {
      ...SeoFields
    }
    createdAt
    updatedAt
  }
  ${SEO_FRAGMENT}
`;

export const POLICY_FRAGMENT = /* GraphQL */ `
  fragment PolicyFields on ShopPolicy {
    id
    title
    handle
    body
    url
  }
`;
